import React, { useMemo, useState } from 'react';
import Select from 'react-select';
import findIndex from 'lodash/findIndex';
import { getTargetId } from '../../utils/helpers';
import targetOptionStyles from './targetOptionStyles';
import { TargetNode } from '../../models/TargetNode';

interface TargetRow {
  _id: string;
  name: string;
  enabled?: boolean;
  // The next-link of that target; a target is "previous" to this one when its
  // c70 points here.
  c70?: string;
}

interface TargetOption {
  value: string;
  label: string;
  enabled?: boolean;
}

interface PreviousSelectorProps {
  node: TargetNode;
  targets: TargetRow[];
  onChange: (option: TargetOption | null) => void;
}

const PreviousSelector = ({ node, targets, onChange }: PreviousSelectorProps) => {
  const id = node._id;

  // Previous targets are those whose c70 link points at this node.
  const options = useMemo<TargetOption[]>(
    () => targets
      .filter((target) => getTargetId(target.c70 as string) == id)
      .map((target) => ({
        value: target._id,
        label: target.name,
        enabled: target.enabled,
      })),
    [targets, id]
  );

  // KNOWN LIMITATION (tracked in SELECTED_NODE_PLAN.md): this seeds in a
  // useState INITIALIZER, so it only auto-selects at mount and only when there
  // is exactly one candidate. A save refetch unmounts and remounts the editor
  // (the isTargetLoading → <Loader/> early return), which is why an explicit
  // pick is lost afterwards. The fix is to derive this from `options` during
  // render with local state only as an override.
  const [selectedOption, setSelectedOption] = useState<TargetOption | null>(() => (
    options.length === 1
      ? { value: options[0].value, label: options[0].label }
      : null
  ));

  const prevTargetName = () => {
    const index = selectedOption ? findIndex(options, { value: selectedOption.value }) : -1;
    return index >= 0 ? options[index].label : '';
  };

  const handleChange = (option: TargetOption | null) => {
    setSelectedOption(option);
    onChange(option); // Hack to update parent
  };

  return (
    <div>
      <Select
        value={selectedOption}
        placeholder={prevTargetName()}
        onChange={handleChange}
        options={options}
        styles={targetOptionStyles}
      />
    </div>
  );
};

export default React.memo(PreviousSelector);
